import React, { useMemo, useState, useContext } from 'react';
import moment from 'moment';
import { flatten } from 'lodash';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useProjects from '@ubeya/shared/hooks/account/useProjects';
import groupByData from 'shared/utils/payroll';
import DetailedInvoicesModal from 'shared/components/Invoices/DetailedInvoicesModal';

const SupplierInvoiceModal = ({
  supplierBranchesIds,
  payroll = [],
  onClose,
  countryCode,
  timeRange,
  filePrefix = ''
}) => {
  const { t, dateFormat } = useContext(ConfigContext);
  const { branches } = useBranches();
  const { mappedLabels } = useLabels();
  const { mappedLocations } = useLocations();

  const [sortingColumns] = useState({});

  const { start, end } = timeRange;

  const { projects, employeesMap } = useProjects({
    timeRange: {
      startDate: start.format(API_DATE_FORMAT),
      endDate: end.format(API_DATE_FORMAT)
    },
    full: true,
    filters: { basic: [{ id: 17, value: supplierBranchesIds }] },
    branchIds: (branches || []).map(({ id }) => id)
  });

  const mapPayroll = useMemo(
    () =>
      payroll.reduce((output, { mappedPayrollByTimesheetId = {} }) => {
        // mappedPayrollByTimesheetId is an object of keys (timesheetIds) and values (payroll value)
        Object.assign(output, mappedPayrollByTimesheetId);
        return output;
      }, {}),
    [payroll]
  );

  const slots = flatten(
    (projects || []).map(
      ({
        id: projectId,
        labelId,
        additionalRevenues,
        locationId,
        branchId,
        name,
        date: projectStartDate,
        endDate: projectEndDate,
        orders
      }) =>
        flatten(
          orders.map(({ shifts }) =>
            flatten(
              shifts
                .filter(({ isOverbooking }) => !isOverbooking)
                .map((shift) =>
                  flatten(
                    (shift.slots || [])
                      .filter(({ employeeSlot, timesheet }) => !!employeeSlot && !!mapPayroll[timesheet.id])
                      .map((slot) => {
                        const projectHasName =
                          (labelId && mappedLabels[labelId]?.title !== mappedLocations[locationId]?.name) || name;

                        const projectName = projectHasName
                          ? labelId
                            ? mappedLabels[labelId]?.title
                            : name
                          : mappedLocations[locationId]?.name;

                        // shift location might be only in supplier account and then mapped location does not contain it
                        // then use project location instead of shift location
                        const projectLocation = mappedLocations[locationId]?.id;
                        const shiftLocation = mappedLocations[shift.locationId]?.id;

                        const slotDate = slot?.startTime
                          ? moment(slot.startTime).format(API_DATE_FORMAT)
                          : shift.date
                          ? moment(shift.date).format(API_DATE_FORMAT)
                          : projectStartDate;
                        const { id: wageId, flatHours = 0, flatWage = 0, hourlyWage = 0 } =
                          mapPayroll[slot.timesheet.id].wage || {};

                        return {
                          ...slot,
                          clientWage: { id: wageId, flatHours, flatWage, hourlyWage },
                          date: slotDate,
                          positionId: shift.positionId,
                          additionalRevenues,
                          locationId: shiftLocation || projectLocation,
                          projectId,
                          branchId,
                          name: projectName,
                          projectStartDate,
                          projectEndDate
                        };
                      })
                  )
                )
            )
          )
        )
    )
  );

  const groupedData = useMemo(
    () =>
      employeesMap
        ? groupByData({
            jobs: [],
            slots,
            employees: Object.values(employeesMap || {}),
            groupByKey: 'project',
            mapPayroll,
            dateFormat,
            sortingColumns,
            summarized: false,
            mappedLocations,
            mappedLabels
          })
        : [],
    [employeesMap, slots, mapPayroll, dateFormat, sortingColumns, mappedLocations, mappedLabels]
  );

  return (
    <DetailedInvoicesModal
      isClient
      onClose={onClose}
      groupedData={groupedData}
      employeesMap={employeesMap}
      countryCode={countryCode}
      title={t('supplierInvoice')}
      timeRange={timeRange}
      filePrefix={filePrefix}
    />
  );
};

export default SupplierInvoiceModal;
